import React, { useState } from 'react'
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { dangerNotification } from '../../redux/slices/ui';

import { usePacienteHistorial } from './hooks/usePacienteHistorial';
import { useConfiguracionConsulta } from './hooks/useConfiguracionConsulta';

import { Error, Forbidden, Loader, PrimaryButton, TextInputPreview, TextInputPreviewHorizontal } from '../../components';

import styles from './PacienteHistorial.module.css';

export const PacienteHistorial = () => {

  let { id } = useParams();

  const dispatch = useDispatch();

  const { permisos } = useSelector(state => state.auth);

  const [downloading, setDownloading] = useState(false);

  const { configuracionConsultaQuery: { isLoading: isLoadingConfiguracion, isError: isErrorConfiguracion, data: configuracionConsulta } } = useConfiguracionConsulta();

  const { pacienteHistorialQuery: { isLoading: isLoadingHistorial, isError: isErrorHistorial, data: historial } } = usePacienteHistorial(id);

  const handleDownload = async () => {
    try {
      setDownloading(true);

      const res = await fetch(
        process.env.REACT_APP_API_URL + `/historial/descargar/${id}`,
        {
          method: "GET",
          headers: {
            'x-token': localStorage.getItem('token'),
          },
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        dispatch(dangerNotification(`Error ${res.status}: ${text || "No se pudo descargar"}`));
      }

      // Intenta obtener el filename del header
      const dispo = res.headers.get("Content-Disposition") || "";
      const match = dispo.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
      const fallbackName = `historial_${id}.pdf`;
      let fileName = fallbackName;

      if (match && match[1]) {
        fileName = match[1].replace(/['"]/g, "");
        // Por si viene RFC5987: filename*=UTF-8''nombre.pdf
        const utf8Name = dispo.match(/filename\*\s*=\s*UTF-8''([^;\n]+)/i);
        if (utf8Name && utf8Name[1]) {
          fileName = decodeURIComponent(utf8Name[1]);
        }
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // console.error(err);
      dispatch(dangerNotification('No se pudo descargar el archivo.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadReceta = async (id) => {
        try {
            setDownloading(true);
    
            const res = await fetch(
                process.env.REACT_APP_API_URL + `/consulta/receta/${id}`,
                {
                    method: "GET",
                    headers: {
                        'x-token': localStorage.getItem('token'),
                    },
                }
            );
    
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                dispatch(dangerNotification(`Error ${res.status}: ${text || "No se pudo descargar"}`));
            }
    
            // Intenta obtener el filename del header
            const dispo = res.headers.get("Content-Disposition") || "";
            const match = dispo.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
            const fallbackName = `Receta.pdf`;
            let fileName = fallbackName;
    
            if (match && match[1]) {
                fileName = match[1].replace(/['"]/g, "");
                // Por si viene RFC5987: filename*=UTF-8''nombre.pdf
                const utf8Name = dispo.match(/filename\*\s*=\s*UTF-8''([^;\n]+)/i);
                if (utf8Name && utf8Name[1]) {
                fileName = decodeURIComponent(utf8Name[1]);
                }
            }
    
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
    
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || fallbackName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            // console.error(err);
            dispatch(dangerNotification('No se pudo descargar el archivo.'));
        } finally {
            setDownloading(false);
        }
    };

  return (permisos.historial || permisos.receta) ? (
    <>
      {(isLoadingConfiguracion || isLoadingHistorial)
        ? <Loader />
        : <>
            {(isErrorConfiguracion || isErrorHistorial)
              ? <Error />
              : <>
                <div className="row mb-3">
                  <div className="col">
                      <div className={styles.header}>Historial</div>
                  </div>
                  {(permisos.historial) &&
                  <div className="col">
                    <div className='float-end'>
                      <PrimaryButton
                        disabled={downloading}
                        icon="Download"
                        iconSize="0.8"
                        iconMargin={5}
                        onClick={handleDownload}
                        text="Descargar"
                        type="button"
                        size={24}
                      />
                    </div>
                  </div>
                  }
                </div>
                <div className="accordion" id="accordionConsultaHistorial">
                  {Object.keys(historial.data).map((fecha, index) => {

                    let date = historial.data[fecha];

                    const collapseId = "collapse" + index;
                    const headingId = "heading" + index;

                    return (
                      <div key={fecha} className="accordion-item">
                        <h2 className="accordion-header" id={headingId}>
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={"#" + collapseId} aria-expanded="false" aria-controls={collapseId}>
                                {(typeof date.fechaFormato !== 'undefined') ? date.fechaFormato : index}
                            </button>
                        </h2>
                        <div id={collapseId} className="accordion-collapse collapse" aria-labelledby={headingId} data-bs-parent="#accordionConsultaHistorial">
                            <div className="accordion-body">
                                {(permisos.historial && typeof date.fechaFormato !== 'undefined') &&
                                <div className="row">
                                    <div className="col">
                                        <TextInputPreviewHorizontal
                                            label="Fecha"
                                            name="fecha"
                                            value={date.fechaFormato}
                                        />
                                    </div>
                                </div>
                                }
                                {(typeof date.consultas !== 'undefined' && date.consultas.length > 0) && <>
                                  {date.consultas.map(consulta => (<>
                                    <div className="row">
                                      <div className="col">
                                        <div className={styles.modalThirdHeader}>Consulta</div>
                                      </div>
                                    </div>
                                    <div className={styles.mainPanel}>
                                      {(configuracionConsulta.historiaClinica.motivoConsulta && typeof consulta.motivo !== 'undefined') &&
                                        <div className="row">
                                          <div className="col">
                                              <TextInputPreviewHorizontal
                                                  label="Motivo de consulta"
                                                  name="motivo"
                                                  value={consulta.motivo}
                                              />
                                          </div>
                                        </div>
                                      }
                                      {(configuracionConsulta.historiaClinica.peea && typeof consulta.peea !== 'undefined') &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Padecimiento"
                                                    name="peea"
                                                    value={consulta.peea}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && ((configuracionConsulta.exploracionFisica.habitusExterior && typeof consulta.exploracionFisica.habitusExterior !== 'undefined') || (configuracionConsulta.exploracionFisica.cabezaCuello && typeof consulta.exploracionFisica.cabezaYCuello !== 'undefined') || (configuracionConsulta.exploracionFisica.torax && typeof consulta.exploracionFisica.torax !== 'undefined') || (configuracionConsulta.exploracionFisica.abdomen && typeof consulta.exploracionFisica.abdomen !== 'undefined') || (configuracionConsulta.exploracionFisica.piel && typeof consulta.exploracionFisica.piel !== 'undefined') || (configuracionConsulta.exploracionFisica.extremidades && typeof consulta.exploracionFisica.extremidades !== 'undefined') || (configuracionConsulta.exploracionFisica.genitales && typeof consulta.exploracionFisica.genitales !== 'undefined') || (configuracionConsulta.exploracionFisica.otro && typeof consulta.exploracionFisica.otro !== 'undefined'))) &&
                                        <div className="row">
                                            <div className="col">
                                                <div className={styles.modalFourthHeader}>Exploración Física</div>
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.habitusExterior !== 'undefined' && configuracionConsulta.exploracionFisica.habitusExterior) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Habitus exterior"
                                                    name="habitusExterior"
                                                    value={consulta.exploracionFisica.habitusExterior}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.cabezaYCuello !== 'undefined' && configuracionConsulta.exploracionFisica.cabezaCuello) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Cabeza y cuello"
                                                    name="cabezaYCuello"
                                                    value={consulta.exploracionFisica.cabezaYCuello}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.torax !== 'undefined' && configuracionConsulta.exploracionFisica.torax) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Tórax"
                                                    name="torax"
                                                    value={consulta.exploracionFisica.torax}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.abdomen !== 'undefined' && configuracionConsulta.exploracionFisica.abdomen) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Abdomen"
                                                    name="abdomen"
                                                    value={consulta.exploracionFisica.abdomen}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.piel !== 'undefined' && configuracionConsulta.exploracionFisica.piel) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Piel"
                                                    name="piel"
                                                    value={consulta.exploracionFisica.piel}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.extremidades !== 'undefined' && configuracionConsulta.exploracionFisica.extremidades) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Extremidades"
                                                    name="extremidades"
                                                    value={consulta.exploracionFisica.extremidades}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.genitales !== 'undefined' && configuracionConsulta.exploracionFisica.genitales) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Genitales"
                                                    name="genitales"
                                                    value={consulta.exploracionFisica.genitales}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.exploracionFisica !== 'undefined' && typeof consulta.exploracionFisica.otro !== 'undefined' && configuracionConsulta.exploracionFisica.otro) &&
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreviewHorizontal
                                                    label="Otro"
                                                    name="otro"
                                                    value={consulta.exploracionFisica.otro}
                                                />
                                            </div>
                                        </div>
                                      }
                                      {(typeof consulta.signosVitales !== 'undefined' && ((configuracionConsulta.signosVitales.frecuenciaCardiaca && typeof consulta.signosVitales.fc !== 'undefined') || (configuracionConsulta.signosVitales.frecuenciaRespiratoria && typeof consulta.signosVitales.fr !== 'undefined') || (configuracionConsulta.signosVitales.presionArterial && typeof consulta.signosVitales.ta !== 'undefined') || (configuracionConsulta.signosVitales.temperatura && typeof consulta.signosVitales.t !== 'undefined') || (configuracionConsulta.signosVitales.sa02 && typeof consulta.signosVitales.saO2 !== 'undefined') || (configuracionConsulta.signosVitales.peso && typeof consulta.signosVitales.peso !== 'undefined') || (configuracionConsulta.signosVitales.talla && typeof consulta.signosVitales.talla !== 'undefined') || (configuracionConsulta.signosVitales.imc && typeof consulta.signosVitales.imc !== 'undefined') || (configuracionConsulta.signosVitales.fem && typeof consulta.signosVitales.fem !== 'undefined'))) &&
                                        <>
                                          <div className="row">
                                              <div className="col">
                                                  <div className={styles.modalFourthHeader}>Signos Vitales</div>
                                              </div>
                                          </div>
                                          <div className="row">
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.fc !== 'undefined' && configuracionConsulta.signosVitales.frecuenciaCardiaca) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="FC"
                                                    name="fc"
                                                    value={consulta.signosVitales.fc + " lpm"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.fr !== 'undefined' && configuracionConsulta.signosVitales.frecuenciaRespiratoria) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="FR"
                                                    name="fr"
                                                    value={consulta.signosVitales.fr + " rpm"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.ta !== 'undefined' && configuracionConsulta.signosVitales.presionArterial) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="TA"
                                                    name="ta"
                                                    value={consulta.signosVitales.ta + " mmHg"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.t !== 'undefined' && configuracionConsulta.signosVitales.temperatura) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="T"
                                                    name="t"
                                                    value={consulta.signosVitales.t + " °C"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.saO2 !== 'undefined' && configuracionConsulta.signosVitales.sa02) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="SaO2"
                                                    name="saO2"
                                                    value={consulta.signosVitales.saO2 + " %"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.peso !== 'undefined' && configuracionConsulta.signosVitales.peso) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="Peso"
                                                    name="peso"
                                                    value={consulta.signosVitales.peso + " Kg"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.talla !== 'undefined' && configuracionConsulta.signosVitales.talla) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="Talla"
                                                    name="talla"
                                                    value={consulta.signosVitales.talla + " cm"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.imc !== 'undefined' && configuracionConsulta.signosVitales.imc) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="IMC"
                                                    name="imc"
                                                    value={consulta.signosVitales.imc + " kg/m2"}
                                                />
                                            </div>
                                            }
                                            {(typeof consulta.signosVitales !== 'undefined' && typeof consulta.signosVitales.fem !== 'undefined' && configuracionConsulta.signosVitales.fem) &&
                                            <div className="col-sm-12 col-md-6 col-lg-4">
                                                <TextInputPreviewHorizontal
                                                    label="FEM"
                                                    name="fem"
                                                    value={consulta.signosVitales.fem + " ml"}
                                                />
                                            </div>
                                            }
                                          </div>
                                        </>
                                      }
                                      {(typeof consulta.diagnosticos !== 'undefined' && consulta.diagnosticos.length > 0) &&
                                        <>
                                          <div className="row">
                                              <div className="col">
                                                  <div className={styles.modalFourthHeader}>Diagnósticos (CIE10)</div>
                                              </div>
                                          </div>
                                          <div className="row">
                                              <div className="col">
                                              { consulta.diagnosticos.map(diag => {

                                                  let diagnostico = '';
                                                  if(typeof diag.diagnostico !== 'undefined' && typeof diag.diagnostico.nombre !== 'undefined' && diag.diagnostico.nombre !== ''){
                                                      diagnostico = diag.diagnostico.nombre;
                                                  }

                                                  return <>
                                                      <div className="row">
                                                          <div className="col">
                                                              <TextInputPreview
                                                                  label=""
                                                                  name="diagnostico"
                                                                  value={diagnostico}
                                                                  showLabel={false}
                                                              />
                                                          </div>
                                                      </div>
                                                  </>
                                              }) }
                                              </div>
                                          </div>
                                        </>
                                      }
                                      {(typeof consulta.diagnosticosData !== 'undefined' && consulta.diagnosticosData !== '') &&
                                      <>
                                        <div className="row">
                                            <div className="col">
                                                <div className={styles.modalFourthHeader}>Diagnóstico</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col">
                                                <TextInputPreview
                                                    label=""
                                                    name="diagnosticosData"
                                                    value={consulta.diagnosticosData}
                                                    showLabel={false}
                                                />
                                            </div>
                                        </div>
                                      </>
                                      }
                                      {(typeof consulta.medicamentos !== 'undefined' && consulta.medicamentos.length > 0) &&
                                      <>
                                        <div className="row">
                                            <div className="col">
                                                <div className={styles.modalFourthHeader}>Medicamentos</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col">
                                            { consulta.medicamentos.map(med => {

                                                let medicamento = '';
                                                if(typeof med.medicamento !== 'undefined' && typeof med.medicamento.nombre !== 'undefined' && med.medicamento.nombre !== ''){
                                                    medicamento = med.medicamento.nombre;
                                                }

                                                return <TextInputPreview
                                                    label=""
                                                    name="medicamentos"
                                                    value={medicamento}
                                                    showLabel={false}
                                                />
                                            }) }
                                            </div>
                                        </div>
                                      </>
                                      }
                                      {(typeof consulta.estudios !== 'undefined' && consulta.estudios.length > 0) &&
                                      <>
                                        <div className="row">
                                            <div className="col">
                                                <div className={styles.modalFourthHeader}>Estudios</div>
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col">
                                            { consulta.estudios.map(est => {
                                                return <TextInputPreview
                                                    label=""
                                                    name="estudios"
                                                    value={(typeof est.nombre !== 'undefined') ? est.nombre : ''}
                                                    showLabel={false}
                                                />
                                            }) }
                                            </div>
                                        </div>
                                      </>
                                      }
                                      {(typeof consulta.comentarios !== 'undefined' && consulta.comentarios !== '') &&
                                      <>
                                      <div className="row">
                                          <div className="col">
                                              <div className={styles.modalFourthHeader}>Comentarios</div>
                                          </div>
                                      </div>
                                      <div className="row">
                                        <div className="col">
                                          <TextInputPreview
                                              label=""
                                              name="comentarios"
                                              value={consulta.comentarios}
                                              showLabel={false}
                                          />
                                        </div>
                                      </div>
                                      </>
                                      }
                                      {(permisos.receta && !consulta.migrada && ((typeof consulta.medicamentos !== 'undefined' && consulta.medicamentos.length > 0) || (typeof consulta.estudios !== 'undefined' && consulta.estudios.length > 0))) &&
                                      <div className="row">
                                        <div className="col">
                                            <div className='float-end'>
                                                <PrimaryButton
                                                    disabled={downloading}
                                                    icon="Download"
                                                    iconSize="0.8"
                                                    iconMargin={5}
                                                    onClick={() => handleDownloadReceta(consulta._id)}
                                                    text="Descargar receta"
                                                    type="button"
                                                    size={24}
                                                />
                                            </div>
                                        </div>
                                      </div>
                                      }
                                    </div>
                                  </>))}
                                </>}
                                {(permisos.historial && typeof date.inmunoterapias !== 'undefined' && date.inmunoterapias.length > 0) && <>
                                  <div className="row">
                                    <div className="col">
                                      <div className={styles.modalThirdHeader}>Inmunoterapias</div>
                                    </div>
                                  </div>
                                  <div className={styles.mainPanel}>
                                    {date.inmunoterapias.map(inmunoterapia => {

                                      let frascoInmunoterapia = '';
                                      if(typeof inmunoterapia.identificador !== 'undefined'){
                                        if(inmunoterapia.identificador.includes('#')){
                                          frascoInmunoterapia = inmunoterapia.identificador;
                                        }else{
                                          frascoInmunoterapia = '# ' + inmunoterapia.identificador;
                                        }
                                      }

                                      let aplicacionesRealizadas = (typeof inmunoterapia.aplicaciones !== 'undefined') ? inmunoterapia.aplicaciones.filter(aplicacion => aplicacion.realizado) : [];
                                      let ultimaAplicacion = null;
                                      if(aplicacionesRealizadas.length > 0){
                                        ultimaAplicacion = aplicacionesRealizadas[aplicacionesRealizadas.length - 1];
                                      }
                                      let ultimaAplicacionValue = '';
                                      if(ultimaAplicacion !== null){
                                        if(typeof ultimaAplicacion.fechaFormato !== 'undefined'){
                                          ultimaAplicacionValue = `${ultimaAplicacion.fechaFormato},`;
                                        }
                                        if(typeof ultimaAplicacion.dosis !== 'undefined'){
                                          ultimaAplicacionValue += `${(ultimaAplicacionValue !== '') ? ' ' : ''}${ultimaAplicacion.dosis}`;
                                          if(typeof inmunoterapia.tipoDosis !== 'undefined'){
                                            ultimaAplicacionValue += (inmunoterapia.tipoDosis === 'gota' && ultimaAplicacion.dosis > 1) ? 'gotas' : ' ' + inmunoterapia.tipoDosis;
                                          }
                                        }
                                        if(typeof inmunoterapia.sitioAplicacion !== 'undefined' && inmunoterapia.sitioAplicacion !== '' && typeof ultimaAplicacion.ladoAplicacion !== 'undefined' && ultimaAplicacion.ladoAplicacion !== ''){
                                          ultimaAplicacionValue += ', ' + inmunoterapia.sitioAplicacion + ' ' + ultimaAplicacion.ladoAplicacion;
                                        }
                                        if(typeof ultimaAplicacion.reaccion !== 'undefined' && ultimaAplicacion.reaccion !== ''){
                                          ultimaAplicacionValue += ', Reacción ' + ultimaAplicacion.reaccion;
                                        }
                                      }

                                      let inmunoterapiaHeader = '';
                                      if(typeof inmunoterapia.tipo !== 'undefined' && typeof inmunoterapia.fase !== 'undefined'){
                                        inmunoterapiaHeader = `${inmunoterapia.tipo} - ${inmunoterapia.fase}`;
                                      }else{
                                        if(typeof inmunoterapia.tipo !== 'undefined'){
                                          inmunoterapiaHeader = inmunoterapia.tipo;
                                        }else if(typeof inmunoterapia.fase !== 'undefined'){
                                          inmunoterapiaHeader = inmunoterapia.fase;
                                        }else{
                                          inmunoterapiaHeader = inmunoterapia._id;
                                        }
                                      }

                                      return (<>
                                        <div className="row">
                                          <div className="col">
                                            <div className={styles.modalFourthHeader}>{ inmunoterapiaHeader }</div>
                                          </div>
                                        </div>
                                        <div className="row">
                                          {(typeof inmunoterapia.identificador !== 'undefined') &&
                                          <div className="col-xs-12 col-sm-12 col-md">
                                            <TextInputPreviewHorizontal
                                              label="Frasco"
                                              name="frasco"
                                              value={frascoInmunoterapia}
                                            />
                                          </div>
                                          }
                                          {(typeof inmunoterapia.concentracion !== 'undefined') &&
                                          <div className="col-xs-12 col-sm-12 col-md">
                                            <TextInputPreviewHorizontal
                                              label="Concentración"
                                              name="concentracion"
                                              value={inmunoterapia.concentracion}
                                            />
                                          </div>
                                          }
                                        </div>
                                        {(typeof inmunoterapia.alergenos !== 'undefined' && inmunoterapia.alergenos.length > 0) &&
                                        <div className="row">
                                          <div className="col">
                                            <div className="row">
                                              <div className="col">
                                                <TextInputPreview
                                                  label="Alergenos"
                                                  name="alergenos"
                                                  value={<div>
                                                    {inmunoterapia.alergenos.map(alergeno => <div>{alergeno.nombre}</div>)}
                                                  </div>}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        }
                                        {(ultimaAplicacion !== null) &&
                                        <div className="row">
                                          <div className="col">
                                            <TextInputPreview
                                              label="última aplicación"
                                              name="ultimaAplicacion"
                                              value={<div>{ ultimaAplicacionValue }</div>}
                                            />
                                          </div>
                                        </div>
                                        }
                                        {(typeof inmunoterapia.comentarios !== 'undefined' && inmunoterapia.comentarios !== '') &&
                                        <div className="row">
                                          <div className="col">
                                            <TextInputPreview
                                              label="Comentarios"
                                              name="comentarios"
                                              value={<div>{ inmunoterapia.comentarios }</div>}
                                            />
                                          </div>
                                        </div>
                                        }
                                      </>);
                                    })}
                                  </div>
                                </>}
                                {(permisos.historial && typeof date.pruebasAlergia !== 'undefined' && date.pruebasAlergia.length > 0) && <>
                                  <div className="row">
                                    <div className="col">
                                      <div className={styles.modalThirdHeader}>Pruebas de alergia</div>
                                    </div>
                                  </div>
                                  <div className={styles.mainPanel}>
                                  {date.pruebasAlergia.map(prueba => {

                                    let pruebaAlergiaHeader = '';
                                    if(typeof prueba.tipoPrueba !== 'undefined' && typeof prueba.prueba !== 'undefined'){
                                      pruebaAlergiaHeader = `${prueba.tipoPrueba} - ${prueba.prueba}`;
                                    }else{
                                      if(typeof prueba.tipoPrueba !== 'undefined'){
                                        pruebaAlergiaHeader = prueba.tipoPrueba;
                                      }else if(typeof prueba.prueba !== 'undefined'){
                                        pruebaAlergiaHeader = prueba.prueba;
                                      }else{
                                        pruebaAlergiaHeader = prueba._id;
                                      }
                                    }

                                    return (<>
                                      <div className="row">
                                        <div className="col">
                                          <div className={styles.modalFourthHeader}>{ pruebaAlergiaHeader }</div>
                                        </div>
                                      </div>
                                      <div className="row">
                                        {(typeof prueba.dispositivo !== 'undefined') &&
                                        <div className="col-xs-12 col-sm-12 col-md">
                                          <TextInputPreviewHorizontal
                                            label="Dispositivo"
                                            name="dispositivo"
                                            value={prueba.dispositivo}
                                          />
                                        </div>
                                        }
                                        {(typeof prueba.sitio !== 'undefined') &&
                                        <div className="col-xs-12 col-sm-12 col-md">
                                          <TextInputPreviewHorizontal
                                            label="Sitio"
                                            name="sitio"
                                            value={prueba.sitio}
                                          />
                                        </div>
                                        }
                                        {(typeof prueba.positiva !== 'undefined') &&
                                        <div className="col-xs-12 col-sm-12 col-md">
                                          <TextInputPreviewHorizontal
                                            label="Positiva"
                                            name="positiva"
                                            value={(prueba.positiva) ? 'Si' : 'No'}
                                          />
                                        </div>
                                        }
                                      </div>
                                      {(prueba.positiva && typeof prueba.alergenos !== 'undefined' && prueba.alergenos.length > 0) &&
                                      <div className="row">
                                        <div className="col">
                                          <TextInputPreview
                                            label="Alergenos"
                                            name="alergenos"
                                            value={<div>
                                              {prueba.alergenos.map(alergeno => <div>{(typeof alergeno.nombre !== 'undefined') ? alergeno.nombre : ''}{(typeof alergeno.resultados !== 'undefined' && alergeno.resultados !== '') ? ' - ' + alergeno.resultados : ''}</div>)}
                                            </div>}
                                          />
                                        </div>
                                      </div>
                                      }
                                      {(typeof prueba.comentarios !== 'undefined' && prueba.comentarios !== '') &&
                                      <div className="row">
                                        <div className="col">
                                          <TextInputPreview
                                            label="Comentarios"
                                            name="comentarios"
                                            value={<div>{ prueba.comentarios }</div>}
                                          />
                                        </div>
                                      </div>
                                      }
                                    </>);
                                  })}
                                  </div>
                                </>}
                                {(permisos.historial && typeof date.laboratorios !== 'undefined' && date.laboratorios.length > 0) && <>
                                  <div className="row">
                                    <div className="col">
                                      <div className={styles.modalThirdHeader}>Laboratorios</div>
                                    </div>
                                  </div>
                                  <div className={styles.mainPanel}>
                                    {date.laboratorios.map(lab => <>
                                      <div className="row">
                                        <div className="col">
                                          <div className={styles.modalFourthHeader}>{ lab.estudios.map(estudio => estudio.nombre).join(', ') }</div>
                                        </div>
                                      </div>
                                      {(typeof lab.comentarios !== 'undefined' && lab.comentarios !== '') &&
                                      <div className="row">
                                        <div className="col">
                                          <TextInputPreview
                                            label="Comentarios"
                                            name="comentarios"
                                            value={lab.comentarios}
                                          />
                                        </div>
                                      </div>
                                      }
                                    </>)}
                                  </div>
                                </>}
                                {(permisos.historial && typeof date.radiologia !== 'undefined' && date.radiologia.length > 0) && <>
                                  <div className="row">
                                    <div className="col">
                                      <div className={styles.modalThirdHeader}>Radiología</div>
                                    </div>
                                  </div>
                                  <div className={styles.mainPanel}>
                                    {date.radiologia.map(rad => <>
                                      <div className="row">
                                        <div className="col">
                                          <div className={styles.modalFourthHeader}>{ rad.estudios.map(estudio => estudio.nombre).join(', ') }</div>
                                        </div>
                                      </div>
                                      {(typeof rad.comentarios !== 'undefined' && rad.comentarios !== '') &&
                                      <div className="row">
                                        <div className="col">
                                          <TextInputPreview
                                            label="Comentarios"
                                            name="comentarios"
                                            value={rad.comentarios}
                                          />
                                        </div>
                                      </div>
                                      }
                                    </>)}
                                  </div>
                                </>}
                            </div>
                        </div>
                      </div>

                    );
                  })}
                </div>
              </>
            }
        </>
    }
    </>
  ) : <Forbidden />
}
